import React from "react";
import { Block, Chip, Label, NumField, TextArea, Note } from "./ui";
import { autoLayer1, direction, DIRECTION_GRID } from "./evalModel";
import { ShotProfilePull } from "./ShotProfilePull";

const STAT_PAIRS = [
  ["3PT", "three_pt_pct", "three_pa_per_game", "3PT %", "3PA / game"],
  ["Catch-and-shoot 3s", "cs_pct", "cs_att", "%", "Attempts"],
  ["Off-the-dribble 3s", "otd_pct", "otd_att", "%", "Attempts"],
  ["C+S 3s · Uncontested", "cs_uncontested_pct", "cs_uncontested_att", "%", "Attempts"],
  ["C+S 3s · Contested", "cs_contested_pct", "cs_contested_att", "%", "Attempts"],
];

// Tier row: auto value (from stats) with coach override.
function TierPick({ L, listName, label, autoId, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}{autoId && <span className="ml-2 text-[#4ade80] normal-case tracking-normal font-normal">auto · {L.nameOf(autoId)}</span>}</Label>
      <div className="flex flex-wrap gap-1.5">
        {L.list(listName).map(t => <Chip key={t.item_id} active={value === t.item_id} onClick={() => onChange(t.item_id)} testid={`${testid}-${t.item_id}`} title={t.details}>{t.name}<span className="opacity-60 ml-1.5 normal-case tracking-normal">{t.description}</span></Chip>)}
      </div>
    </div>
  );
}

// Layer 1 · Step 1: game numbers -> 1A volume tier · 1B % tier · 1C development direction.
export function Step1GameStats({ L, ev, set, playerId, gamesPlayed }) {
  const l1 = ev.layer1, gs = l1.stats;
  // Recompute auto values from stats; keep coach overrides (fields that differ from the previous auto value).
  const applyStats = (stats) => {
    const auto = autoLayer1(stats);
    const next = { ...l1, stats, auto };
    for (const k of ["volume_tier_id", "pct_tier_id", "direction_id"]) if (!l1[k] || l1[k] === l1.auto[k]) next[k] = auto[k];
    const same = [...(l1.auto.stat_reading_ids || [])].sort().join() === [...l1.stat_reading_ids].sort().join();
    if (same || !l1.stat_reading_ids.length) next.stat_reading_ids = auto.stat_reading_ids;
    next.shot_type_plans = auto.shot_type_plans;
    set({ layer1: next });
  };
  const setGs = (k, v) => applyStats({ ...gs, [k]: v });
  const setL1 = (patch) => set({ layer1: { ...l1, ...patch } });
  const dir = l1.direction_id ? L.itemById[l1.direction_id] : null;
  const followDir = !l1.direction_id || l1.direction_id === l1.auto.direction_id;

  return (
    <Block kicker="Layer 1 · Results · Step 1" title="Game Stats" subtitle="Game film if there are no stats" testid="se-step1"
      right={<div className="flex items-center gap-3 flex-wrap">
        <ShotProfilePull playerId={playerId} gamesPlayed={gamesPlayed} onPull={(vals) => applyStats({ ...gs, ...vals, source: "Game stats" })} />
        <div className="flex gap-1">{["Game stats", "Game film"].map(s => <Chip key={s} active={gs.source === s} onClick={() => setL1({ stats: { ...gs, source: s } })} testid={`se-source-${s === "Game stats" ? "stats" : "film"}`}>{s}</Chip>)}</div>
      </div>}>
      {gs.shot_diet_id && <div className="text-[10px] uppercase tracking-[0.2em] text-[#4ade80]" data-testid="se-pulled-note">Numbers pulled from the Shot Diet Shot Profile — edit any field to override</div>}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STAT_PAIRS.map(([label, pk, ak, pl, al]) => (
          <div key={pk} className="border border-white/5 bg-[#101010] p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white font-bold">{label}</div>
            <NumField label={pl} value={gs[pk]} onChange={v => setGs(pk, v)} testid={`se-${pk}`} />
            <NumField label={al} value={gs[ak]} onChange={v => setGs(ak, v)} testid={`se-${ak}`} />
          </div>
        ))}
      </div>
      <TextArea label="Notes" value={gs.notes} onChange={v => setL1({ stats: { ...gs, notes: v } })} testid="se-step1-notes" placeholder="What the film / numbers say" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 border-t border-white/10 pt-4">
        <TierPick L={L} listName="3PT Volume" label="1A · Do they shoot 3s?" autoId={l1.auto.volume_tier_id} value={l1.volume_tier_id} onChange={v => setL1({ volume_tier_id: v, direction_id: followDir ? direction(v, l1.pct_tier_id) : l1.direction_id })} testid="se-vol" />
        <TierPick L={L} listName="3PT % Tier" label="1B · Do they make shots?" autoId={l1.auto.pct_tier_id} value={l1.pct_tier_id} onChange={v => setL1({ pct_tier_id: v, direction_id: followDir ? direction(l1.volume_tier_id, v) : l1.direction_id })} testid="se-pct" />
      </div>

      <div data-testid="se-direction">
        <Label>1C · What it tells us{l1.auto.direction_id && <span className="ml-2 text-[#4ade80] normal-case tracking-normal font-normal">auto · {L.nameOf(l1.auto.direction_id)}</span>}</Label>
        <div className="grid grid-cols-[auto_1fr_1fr] gap-1.5 text-xs">
          <div />
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] text-center">Low volume · VOL-0/1</div>
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] text-center">High volume · VOL-2/3</div>
          {DIRECTION_GRID.map((row, r) => (
            <React.Fragment key={r}>
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] self-center pr-2 [writing-mode:vertical-rl] rotate-180">{r === 0 ? "High % · PCT-2/3" : "Low % · PCT-0/1"}</div>
              {row.map(id => { const it = L.itemById[id]; const on = l1.direction_id === id; return (
                <button key={id} type="button" onClick={() => setL1({ direction_id: id })} data-testid={`se-dev-${id}`} aria-pressed={on}
                  className={`text-left border p-3 transition-colors ${on ? "border-[var(--org-accent)] bg-[var(--org-accent)]/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">{id}</div>
                  <div className={`font-heading uppercase text-base ${on ? "text-[var(--org-accent)]" : "text-white"}`}>{it?.name}</div>
                </button>); })}
            </React.Fragment>
          ))}
        </div>
        {dir && <div className="mt-3 border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5 p-4" data-testid="se-dev-detail"><div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] font-bold mb-1">What we do · {dir.name}</div><div className="text-sm text-white">{dir.description}</div></div>}
        <div className="mt-2"><Note>No labels. Every player gets a plan based on what their stats say.</Note></div>
      </div>
    </Block>
  );
}
